/**
 * In-house error reporter — taps the same events Sentry intercepts
 * and POSTs them to the shaka-api `/api/v1/errors/report/` sink. Runs
 * alongside Sentry, not in place of it. The local sink feeds the
 * shaka-admin triage UI and the AI-review flow.
 *
 * Design rules:
 *   - **Tap, do not swallow** — every handler re-throws / forwards to
 *     the next listener so React + Sentry + DevTools keep working.
 *   - **Client-side dedup** — identical errors fired in a 60-second
 *     window only ship one report. Saves bandwidth when a render
 *     loop spams the same exception.
 *   - **Batching** — reports are queued and flushed on an idle tick.
 *   - **Best-effort** — `fetch` failures here are silently swallowed.
 *     If the sink is down we still have Sentry.
 *   - **No PII** — `breadcrumbs` and `context` are scrubbed before
 *     send. We reuse the same `scrubMapping` policy as the Sentry
 *     init so the two sinks agree on what is sensitive.
 *
 * Mount once in main.tsx after `initSentry()`.
 */

import { API_BASE_URL } from '@/shared/services/api'
import { _scrubMapping as scrubMapping } from './sentry'

type AppSource = 'deck' | 'admin'

interface StackFrame {
  file?: string
  function?: string
  line?: number
  column?: number
}

interface Breadcrumb {
  timestamp: string
  category: string
  message?: string
  data?: unknown
}

interface ReporterContext {
  route?: string
  release?: string
  activeContext?: string
  tenantSchema?: string
  userId?: string
  userEmail?: string
  [key: string]: unknown
}

interface ReportPayload {
  app: AppSource
  name: string
  message: string
  stack: StackFrame[]
  breadcrumbs: Breadcrumb[]
  context: ReporterContext
  occurredAt: string
  sentryEventId?: string
}

// ── State held module-scope ────────────────────────────────────────

let installed = false
let appSource: AppSource = 'deck'
let endpoint: string = ''

const breadcrumbs: Breadcrumb[] = []
const MAX_BREADCRUMBS = 50

const recentFingerprints = new Map<string, number>()
const DEDUP_WINDOW_MS = 60_000

// Benign browser notices that surface on the global `error` event with
// no real exception behind them (no `event.error`, no stack). The
// ResizeObserver "loop" notice is a spec-defined signal that the
// observer deferred notifications to the next frame — zero user impact.
// Dropped so they don't pollute the triage sink.
const IGNORED_MESSAGES: RegExp[] = [
  /ResizeObserver loop completed with undelivered notifications/,
  /ResizeObserver loop limit exceeded/,
]

let flushTimer: number | null = null
const queue: ReportPayload[] = []

// Mutable bag callers populate via `setReporterContext()`. The user/
// tenant/route values move with navigation and login, so the
// reporter reads them lazily on each report rather than capturing
// them at install time.
let dynamicContext: ReporterContext = {}

// ── Public API ──────────────────────────────────────────────────────

export function installErrorReporter(opts?: {
  app?: AppSource
  endpoint?: string
}): void {
  if (installed) return
  installed = true
  appSource = opts?.app ?? 'deck'
  endpoint = (opts?.endpoint ?? `${API_BASE_URL}/api/v1/errors/report/`).replace(
    /\/+$/,
    '',
  ) + '/'

  // window.onerror — synchronous exceptions not caught by React.
  window.addEventListener('error', (event) => {
    if (IGNORED_MESSAGES.some((re) => re.test(event.message))) return
    const error = event.error ?? new Error(event.message)
    capture(error, { type: 'window.onerror' })
  })

  // Unhandled promise rejections — typical for async fetch errors
  // not awaited.
  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason
    const error = reason instanceof Error ? reason : new Error(String(reason))
    capture(error, { type: 'unhandledrejection' })
  })

  // Cheap breadcrumb collectors. Each appends to the rolling buffer;
  // the buffer is included with every report so the AI brief has
  // navigation / click context to reason about.
  installBreadcrumbCollectors()
}

/** Reporter API for non-listener call sites — error boundary, api
 * client catch blocks, manual report. */
export function reportError(
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  if (!installed) return
  const err = error instanceof Error ? error : new Error(String(error))
  capture(err, { type: 'manual', ...extra })
}

export function setReporterContext(partial: ReporterContext): void {
  dynamicContext = { ...dynamicContext, ...partial }
}

export function addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'>): void {
  pushBreadcrumb({ ...breadcrumb, timestamp: new Date().toISOString() })
}

// ── Capture pipeline ────────────────────────────────────────────────

function capture(error: Error, extra: Record<string, unknown> = {}): void {
  const name = error.name || 'Error'
  const message = error.message || ''
  const stack = parseStack(error)
  const fingerprint = clientFingerprint(name, message, stack)
  const now = Date.now()

  // Dedup window — 60s. Drop the report (Sentry still keeps it).
  const lastSeen = recentFingerprints.get(fingerprint)
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return
  }
  recentFingerprints.set(fingerprint, now)

  // Periodic cleanup of the dedup map so it doesn't grow unbounded.
  if (recentFingerprints.size > 100) {
    for (const [key, ts] of recentFingerprints) {
      if (now - ts > DEDUP_WINDOW_MS) recentFingerprints.delete(key)
    }
  }

  const payload: ReportPayload = {
    app: appSource,
    name,
    message,
    stack,
    breadcrumbs: scrubMapping(breadcrumbs) as Breadcrumb[],
    context: {
      ...(scrubMapping(dynamicContext) as ReporterContext),
      route: window.location.pathname + window.location.search,
      ...(extra as ReporterContext),
    },
    occurredAt: new Date().toISOString(),
  }

  queue.push(payload)
  scheduleFlush()
}

function scheduleFlush(): void {
  if (flushTimer != null) return
  // Use setTimeout instead of requestIdleCallback so it runs in
  // environments without it (jsdom in tests, older Safari).
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    void flush()
  }, 250)
}

async function flush(): Promise<void> {
  while (queue.length > 0) {
    const payload = queue.shift()
    if (!payload) continue
    try {
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch {
      // Silent — the sink may be down or CORS may reject. Sentry
      // already captured the error, dropping this report is fine.
      // Skip the failed item and keep attempting the rest of the queue.
      continue
    }
  }
}

// ── Breadcrumb collection ───────────────────────────────────────────

function installBreadcrumbCollectors(): void {
  // Navigation — popstate + pushState shim. Captures route changes
  // from react-router.
  window.addEventListener('popstate', () => {
    pushBreadcrumb({
      timestamp: new Date().toISOString(),
      category: 'navigation',
      message: window.location.pathname,
    })
  })
  patchHistoryMethod('pushState')
  patchHistoryMethod('replaceState')

  // Clicks — element selector for context, no PII (text content NOT
  // captured because it can contain personal data).
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      pushBreadcrumb({
        timestamp: new Date().toISOString(),
        category: 'click',
        message: describeElement(target),
      })
    },
    { capture: true },
  )

  // Console errors — useful for "this warning preceded the crash"
  // diagnostics. We don't override console.error completely; we
  // wrap and forward.
  const originalError = console.error
  console.error = (...args: unknown[]) => {
    pushBreadcrumb({
      timestamp: new Date().toISOString(),
      category: 'console',
      message: args.map((a) => (typeof a === 'string' ? a : safeStringify(a))).join(' ').slice(0, 200),
    })
    return originalError.apply(console, args as never[])
  }
}

function patchHistoryMethod(method: 'pushState' | 'replaceState'): void {
  const original = history[method].bind(history)
  history[method] = function patched(...args: unknown[]) {
    const result = (original as (...a: unknown[]) => unknown)(...args)
    pushBreadcrumb({
      timestamp: new Date().toISOString(),
      category: 'navigation',
      message: window.location.pathname,
    })
    return result
  } as typeof history.pushState
}

function pushBreadcrumb(crumb: Breadcrumb): void {
  breadcrumbs.push(crumb)
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS)
  }
}

function describeElement(el: Element): string {
  const id = el.id ? `#${el.id}` : ''
  const cls = el.classList.length
    ? '.' + [...el.classList].slice(0, 2).join('.')
    : ''
  const role = el.getAttribute('role')
  const roleAttr = role ? `[role=${role}]` : ''
  return `${el.tagName.toLowerCase()}${id}${cls}${roleAttr}`.slice(0, 120)
}

// ── Helpers ──────────────────────────────────────────────────────────

function parseStack(error: Error): StackFrame[] {
  const stack = (error.stack || '').split('\n').slice(0, 30)
  const out: StackFrame[] = []
  for (const line of stack) {
    // V8 / Chrome: "    at functionName (file:line:column)"
    const v8 = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/)
    if (v8) {
      out.push({
        function: v8[1],
        file: v8[2],
        line: Number(v8[3]),
        column: Number(v8[4]),
      })
      continue
    }
    // Spidermonkey / Firefox: "functionName@file:line:column"
    const sm = line.match(/(.+?)@(.+?):(\d+):(\d+)/)
    if (sm) {
      out.push({
        function: sm[1],
        file: sm[2],
        line: Number(sm[3]),
        column: Number(sm[4]),
      })
      continue
    }
  }
  return out
}

function clientFingerprint(name: string, message: string, stack: StackFrame[]): string {
  const normalized = message
    .replace(/\b[a-f0-9]{8,}(-[a-f0-9]+)*\b/gi, '<id>')
    .replace(/\b\d{4,}\b/g, '<n>')
    .slice(0, 200)
  const frames = stack
    .slice(0, 3)
    .map((f) => `${f.file ?? ''}:${f.function ?? ''}`)
    .join('/')
  return `${name}|${normalized}|${frames}`
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
